import api, { materialwatchApi, portalApi, taskManagerApi } from './axiosInstance';

// Auth
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
  // Dev User Switcher — login/identity is Portal-only (see MaterialWatch's
  // own routes/auth.js, which has no /auth/dev-switch route at all). The
  // button lives inside the MaterialWatch module's Sidebar, so this always
  // goes through portalApi: same-origin on the Portal build, a real
  // cross-origin call to Portal's backend on the MaterialWatch build (same
  // pattern as userApi/workerApi below).
  devSwitch: (target) => portalApi.post('/auth/dev-switch', { target }),
  // Public — no session exists yet (see the "set your password" email flow).
  validateSetupToken: (token) => api.get(`/auth/set-password/${token}`),
  setPassword: (data) => api.post('/auth/set-password', data),
};

// Users — Portal-owned data, called via portalApi (see axiosInstance.js): on
// the MaterialWatch build this is a direct cross-origin call to the Portal
// backend (e.g. AuditLog.jsx's user filter dropdown), since MaterialWatch's
// own backend has no /users route at all.
export const userApi = {
  list: (params) => portalApi.get('/users', { params }),
  create: (data) => portalApi.post('/users', data),
  getOne: (id) => portalApi.get(`/users/${id}`),
  update: (id, data) => portalApi.put(`/users/${id}`, data),
  toggleActive: (id) => portalApi.patch(`/users/${id}/toggle-active`),
  resetPassword: (id, data) => portalApi.post(`/users/${id}/reset-password`, data),
  setModuleAccess: (id, data) => portalApi.put(`/users/${id}/module-access`, data),
  bulkGrantModuleAccess: (data) => portalApi.post('/users/bulk-module-access', data),
  getPasswordSetupEmailTemplate: (mode) => portalApi.get('/users/password-setup-email-template', { params: { mode } }),
  sendPasswordSetupEmailTest: (data) => portalApi.post('/users/password-setup-email-test', data),
  bulkSendPasswordSetupEmails: (data) => portalApi.post('/users/bulk-password-setup-emails', data),
  getBulkMailProgress: () => portalApi.get('/users/bulk-password-setup-emails/progress'),
  remove: (id) => portalApi.delete(`/users/${id}`),
  // Do NOT set Content-Type manually — axios/browser must auto-generate the
  // multipart boundary; overriding it strips the boundary and breaks multer.
  bulkUpload:       (formData) => portalApi.post('/users/bulk-upload', formData),
  downloadTemplate: ()         => portalApi.get('/users/template', { responseType: 'blob' }),
  exportData:       ()         => portalApi.get('/users/export', { responseType: 'blob' }),
  // Populates the "assign to site" dropdown — Site lives in MaterialWatch's
  // own DB, so this proxies an internal lookup (see Portal backend's
  // listSites handler).
  listSites: () => portalApi.get('/users/sites'),
};

// Permission Groups (RBAC) — MaterialWatch-owned data (members are Workers,
// not Portal Users), called via materialwatchApi (see axiosInstance.js): on
// the Portal build this is a direct cross-origin call to the MaterialWatch
// backend, since Portal's own backend has no /permission-groups route at all.
export const permissionGroupApi = {
  list: () => materialwatchApi.get('/permission-groups'),
  create: (data) => materialwatchApi.post('/permission-groups', data),
  getOne: (id) => materialwatchApi.get(`/permission-groups/${id}`),
  update: (id, data) => materialwatchApi.put(`/permission-groups/${id}`, data),
  setDefault: (id) => materialwatchApi.post(`/permission-groups/${id}/set-default`),
  remove: (id) => materialwatchApi.delete(`/permission-groups/${id}`),
  // Super Admin only — toggles audit_log_view on the Full Access group.
  // No other group/permission can be touched through this call.
  toggleAuditLogView: (id, enabled) => materialwatchApi.patch(`/permission-groups/${id}/audit-log-view`, { enabled }),
};

// Groups
export const groupApi = {
  list: () => api.get('/groups'),
  create: (data) => api.post('/groups', data),
  getOne: (id) => api.get(`/groups/${id}`),
  update: (id, data) => api.put(`/groups/${id}`, data),
  remove: (id) => api.delete(`/groups/${id}`),
};

// Sites
export const siteApi = {
  list: (params) => api.get('/sites', { params }),
  create: (data) => api.post('/sites', data),
  getOne: (id) => api.get(`/sites/${id}`),
  update: (id, data) => api.put(`/sites/${id}`, data),
  remove: (id) => api.delete(`/sites/${id}`),
  inventorySummary: (id) => api.get(`/sites/${id}/inventory-summary`),
  // Do NOT set Content-Type manually — axios/browser must auto-generate the
  // multipart boundary; overriding it strips the boundary and breaks multer.
  bulkUpload:       (formData) => api.post('/sites/bulk-upload', formData),
  downloadTemplate: ()         => api.get('/sites/template', { responseType: 'blob' }),
};

// Safety Kits
export const safetyKitApi = {
  list: (params) => api.get('/safety-kits', { params }),
  create: (data) => api.post('/safety-kits', data),
  getOne: (id) => api.get(`/safety-kits/${id}`),
  update: (id, data) => api.put(`/safety-kits/${id}`, data),
  remove: (id, deleteOpenRequirements = false) => api.delete(`/safety-kits/${id}`, { data: { deleteOpenRequirements } }),
  checkDelete: (id) => api.get(`/safety-kits/${id}/check-delete`),
  toggleActive: (id) => api.patch(`/safety-kits/${id}/toggle-active`),
  // Do NOT set Content-Type manually — axios/browser must auto-generate the
  // multipart boundary; overriding it strips the boundary and breaks multer.
  bulkUpload:       (formData) => api.post('/safety-kits/bulk-upload', formData),
  downloadTemplate: (type)     => api.get('/safety-kits/template', { params: { type }, responseType: 'blob' }),
};

// Inventory
export const inventoryApi = {
  list: (params) => api.get('/inventory', { params }),
  create: (data) => api.post('/inventory', data),
  getOne: (id) => api.get(`/inventory/${id}`),
  update: (id, data) => api.put(`/inventory/${id}`, data),
  remove: (id) => api.delete(`/inventory/${id}`),
  getBySite: (siteId) => api.get(`/inventory/site/${siteId}`),
  stockHistory: (params) => api.get('/inventory/stock-history', { params }),
  // Do NOT set Content-Type manually on bulkStockUpload — axios/browser must
  // auto-generate the multipart boundary; overriding it strips the boundary
  // and breaks multer.
  bulkStockTemplate: (siteId) => api.get('/inventory/bulk-stock-template', { params: { siteId }, responseType: 'blob' }),
  bulkStockUpload:   (siteId, formData, dryRun = false) => api.post('/inventory/bulk-stock-upload', formData, { params: { siteId, dryRun: dryRun ? '1' : undefined } }),
};

// Workers — MaterialWatch-owned data, called via materialwatchApi (see
// axiosInstance.js): on the Portal build this is a direct cross-origin call
// to the MaterialWatch backend, since Portal's own backend has no /workers
// route at all.
export const workerApi = {
  list:            (params)       => materialwatchApi.get('/workers', { params }),
  lookup:          (employeeId)   => materialwatchApi.get('/workers/lookup', { params: { employeeId } }),
  search:          (q)            => materialwatchApi.get('/workers/search', { params: { q } }),
  create:          (data)         => materialwatchApi.post('/workers', data),
  getOne:          (id)           => materialwatchApi.get(`/workers/${id}`),
  update:          (id, data)     => materialwatchApi.put(`/workers/${id}`, data),
  toggleActive:    (id)           => materialwatchApi.patch(`/workers/${id}/toggle-active`),
  // Soft-delete flow
  checkDelete:     (id)           => materialwatchApi.get(`/workers/${id}/check-delete`),
  remove:          (id, deductAndDelete = false) => materialwatchApi.delete(`/workers/${id}`, { data: { deductAndDelete } }),
  // Trash management
  trash:           ()             => materialwatchApi.get('/workers/trash'),
  restore:         (id)           => materialwatchApi.post(`/workers/${id}/restore`),
  permanentDelete: (id)           => materialwatchApi.delete(`/workers/${id}/permanent`),
  // Do NOT set Content-Type manually — axios/browser must auto-generate the
  // multipart boundary; overriding it strips the boundary and breaks multer.
  bulkUpload:          (formData) => materialwatchApi.post('/workers/bulk-upload', formData),
  downloadTemplate:    ()         => materialwatchApi.get('/workers/template', { responseType: 'blob' }),
  exportData:          (employeeType) => materialwatchApi.get('/workers/export', { params: { employeeType }, responseType: 'blob' }),
  getPermissionGroup:  (id)       => materialwatchApi.get(`/workers/${id}/permission-group`),
  setPermissionGroup:  (id, data) => materialwatchApi.put(`/workers/${id}/permission-group`, data),
  downloadSlip:        (id, params) => materialwatchApi.get(`/workers/${id}/slip`, { params, responseType: 'blob' }),
};

// Worker Site Assignments
export const workerSiteAssignmentApi = {
  list:     (params) => api.get('/worker-site-assignments', { params }),
  create:   (data)   => api.post('/worker-site-assignments', data),
  bulkCreate: (data) => api.post('/worker-site-assignments/bulk', data),
  transfer: (data)   => api.post('/worker-site-assignments/transfer', data),
  remove:   (workerId, siteId) => api.delete(`/worker-site-assignments/${workerId}/${siteId}`),
};

// Worker Requests — employee-initiated Material/Stationery requests, distinct
// from Requirement (a site-vs-central-store procurement model, see
// requirements.js). MaterialWatch-owned, called via materialwatchApi.
export const workerRequestApi = {
  list:     (params) => materialwatchApi.get('/worker-requests', { params }),
  create:   (data)   => materialwatchApi.post('/worker-requests', data),
  withdraw: (id)     => materialwatchApi.patch(`/worker-requests/${id}/withdraw`),
  issue:    (id, data) => materialwatchApi.post(`/worker-requests/${id}/issue`, data),
  preview:  (params) => materialwatchApi.get('/worker-requests/preview', { params }),
  reply:    (id, data) => materialwatchApi.patch(`/worker-requests/${id}/reply`, data),
  close:    (id, data) => materialwatchApi.patch(`/worker-requests/${id}/close`, data),
};

// Site Correction Requests — a worker self-reporting "my assigned site is
// wrong", reviewed by whoever holds site_correction_review. MaterialWatch-
// owned, called via materialwatchApi.
export const siteCorrectionApi = {
  create:  (data) => materialwatchApi.post('/site-correction-requests', data),
  myStatus: ()    => materialwatchApi.get('/site-correction-requests/me'),
  list:    ()     => materialwatchApi.get('/site-correction-requests'),
  approve: (id)   => materialwatchApi.post(`/site-correction-requests/${id}/approve`),
  dismiss: (id)   => materialwatchApi.post(`/site-correction-requests/${id}/dismiss`),
};

// Damage Reports — flag an issued item Damaged with photo evidence, pending
// damage_approval review before the underlying item is actually marked
// Damaged. MaterialWatch-owned, called via materialwatchApi.
export const damageReportApi = {
  list:    (params) => materialwatchApi.get('/damage-reports', { params }),
  // formData: multipart with workerKitId|stationeryIssuanceId, remarks?, photos[].
  // Do NOT set a Content-Type header here — axios/browser sets the multipart
  // boundary; overriding it strips the boundary and breaks multer.
  create:  (formData) => materialwatchApi.post('/damage-reports', formData),
  approve: (id)        => materialwatchApi.post(`/damage-reports/${id}/approve`),
  reject:  (id, data)  => materialwatchApi.post(`/damage-reports/${id}/reject`, data),
  withdraw: (id)       => materialwatchApi.post(`/damage-reports/${id}/withdraw`),
  // formData: multipart with photos[] — attach photo(s) while still PENDING.
  addPhotos: (id, formData) => materialwatchApi.post(`/damage-reports/${id}/photos`, formData),
};

// Worker Kits
// Material Units — individually-serialized inventory, only used for items
// with requiresMaterialId=true. Called via materialwatchApi.
export const materialUnitApi = {
  list: (params) => materialwatchApi.get('/material-units', { params }),
  // Direct batch lookup by id — e.g. to resolve the Material ID codes for a
  // RepairTicket/DamageReport's materialUnitIds.
  byIds: (ids) => materialwatchApi.get('/material-units', { params: { ids: ids.join(',') } }),
  // Full per-asset lifecycle log (issue/return/repair-pipeline/damage events)
  // for one serialized unit — only meaningful for requiresMaterialId items.
  history: (id) => materialwatchApi.get(`/material-units/${id}/history`),
  // Material Information values for this specific unit — one row per key
  // defined on its material type (see materialInfoApi below), with this
  // unit's own value for each (null if never set).
  getInfo: (id) => materialwatchApi.get(`/material-units/${id}/info`),
  updateInfo: (id, data) => materialwatchApi.put(`/material-units/${id}/info`, data),
  // Assign Material IDs to stock that already exists but was never tracked
  // (e.g. requiresMaterialId turned on after stock was added, or an old
  // issuance returned before tracking existed) — one row created per code,
  // capped server-side at the real untracked shortfall. Does NOT add new
  // stock; use inventoryApi.create/stationeryApi.adjustInventory for that.
  create: (data) => materialwatchApi.post('/material-units', data),
  // Fix a typo'd/incorrect Material ID on an existing unit.
  rename: (id, data) => materialwatchApi.put(`/material-units/${id}`, data),
  // Item Lookup — one item across every site (or one selected site) at
  // once: every serialized unit + current holder for a requiresMaterialId
  // item, or aggregate in-stock/issued per site otherwise. params: exactly
  // one of {kitId|stationeryItemId|itItemId}, optional siteId.
  overview: (params) => materialwatchApi.get('/material-units/overview', { params }),
};

// Material Information field definitions — the set of custom keys (e.g.
// "RAM", "Serial Number") tracked for a material/stationery type. Shared by
// every individually-tracked unit of that type; managed only from Master
// Data. Per-unit values live on materialUnitApi.getInfo/updateInfo above.
export const materialInfoApi = {
  listFields: (params) => materialwatchApi.get('/material-info-fields', { params }),
  createField: (data) => materialwatchApi.post('/material-info-fields', data),
  updateField: (id, data) => materialwatchApi.put(`/material-info-fields/${id}`, data),
  deleteField: (id) => materialwatchApi.delete(`/material-info-fields/${id}`),
};

// Repair & Maintenance — Stationery Durables only. 3-step pipeline: create()
// = "Mark as Repaired" (Step 1), sendForMaintenance() (Step 2),
// returnFromMaintenance() (Step 3, restocks), scrap() = "Beyond Recovery"
// (available from either open state).
export const repairTicketApi = {
  list:  (params) => api.get('/repair-tickets', { params }),
  // data: { stationeryIssuanceId } OR { stationeryInventoryId, quantity, materialUnitIds? }, remarks?
  create: (data) => api.post('/repair-tickets', data),
  sendForMaintenance:    (id) => api.post(`/repair-tickets/${id}/send-for-maintenance`),
  returnFromMaintenance: (id) => api.post(`/repair-tickets/${id}/return-from-maintenance`),
  scrap:                 (id) => api.post(`/repair-tickets/${id}/scrap`),
};

export const workerKitApi = {
  list: (params) => api.get('/worker-kits', { params }),
  create: (data) => api.post('/worker-kits', data),
  getOne: (id) => api.get(`/worker-kits/${id}`),
  update: (id, data) => api.put(`/worker-kits/${id}`, data),
  returnKit: (id, data) => api.patch(`/worker-kits/${id}/return`, data),
  remove: (id) => api.delete(`/worker-kits/${id}`),
  checkValidity: (data) => api.post('/worker-kits/check-validity', data),
};

// Requirements
export const requirementApi = {
  list: (params) => api.get('/requirements', { params }),
  create: (data) => api.post('/requirements', data),
  getOne: (id) => api.get(`/requirements/${id}`),
  update: (id, data) => api.put(`/requirements/${id}`, data),
  remove: (id) => api.delete(`/requirements/${id}`),
  cancel: (id) => api.patch(`/requirements/${id}/cancel`),
  getSuggestions: (id) => api.get(`/requirements/${id}/suggestions`),
  refreshSuggestions: (id) => api.post(`/requirements/${id}/suggestions/refresh`),
};

// Transfers
export const transferApi = {
  list: (params) => api.get('/transfers', { params }),
  create: (data) => api.post('/transfers', data),
  getOne: (id) => api.get(`/transfers/${id}`),
  approve: (id) => api.patch(`/transfers/${id}/approve`),
  send: (id) => api.patch(`/transfers/${id}/send`),
  receive: (id) => api.patch(`/transfers/${id}/receive`),
  cancel: (id) => api.delete(`/transfers/${id}`),
};

// Notifications
// Notifications are MaterialWatch-owned data (worker item issued/returned,
// requests, damage approvals, etc.) — Portal's own backend has no
// /notifications route at all. On the Portal build this must cross-origin
// call MaterialWatch's backend (same pattern as workerApi/userApi above);
// on the MaterialWatch build itself, materialwatchApi falls back to the
// same-origin call this always used to be. Using the plain `api` instance
// here previously meant every poll 404'd against Portal's own backend for
// as long as any user was logged in — see NotificationContext.jsx's
// module-access gate for the other half of this fix.
export const notificationApi = {
  list: (params) => materialwatchApi.get('/notifications', { params }),
  markRead: (id) => materialwatchApi.patch(`/notifications/${id}/read`),
  markAllRead: () => materialwatchApi.patch('/notifications/read-all'),
  remove: (id) => materialwatchApi.delete(`/notifications/${id}`),
  unreadCount: () => materialwatchApi.get('/notifications/unread-count'),
};

// Task Manager's own notification summary (chat + task-assigned/submitted
// events + live-computed overdue) — used purely for the badge on Portal's
// Task Manager launcher tile. See task-manager backend's
// GET /api/notifications/summary.
export const taskManagerNotificationApi = {
  summary: () => taskManagerApi.get('/notifications/summary'),
};

// Procurement Fulfillments
export const procurementApi = {
  list:    (params) => api.get('/procurements', { params }),
  create:  (data)   => api.post('/procurements', data),
  receive: (id)     => api.patch(`/procurements/${id}/receive`),
  cancel:  (id)     => api.delete(`/procurements/${id}`),
};

// Audit Logs
export const auditLogApi = {
  list: (params) => api.get('/audit-logs', { params }),
  getOne: (id) => api.get(`/audit-logs/${id}`),
  logModuleEntry: (data) => api.post('/audit-logs/module-entry', data),
};

// Salary Deductions
export const salaryDeductionApi = {
  list: (params) => api.get('/salary-deductions', { params }),
  currentCycle: () => api.get('/salary-deductions/current-cycle'),
};

// Dashboard Analytics
export const dashboardApi = {
  stats: (params) => api.get('/dashboard/stats', { params }),
};

// Site History
export const siteHistoryApi = {
  list: (params) => api.get('/site-history', { params }),
};

// Asset History — read-only archive of items no longer operationally active
// (Damaged, Scrapped, Validity Over). Site-scoping for ASSIGNED_ONLY users is
// enforced server-side; no siteId param needed.
export const assetHistoryApi = {
  list: (category, params) => api.get('/asset-history', { params: { category, ...params } }),
};

// Stationery
export const stationeryApi = {
  // Items
  listItems: (params) => api.get('/stationery/items', { params }),
  createItem: (data) => api.post('/stationery/items', data),
  updateItem: (id, data) => api.put(`/stationery/items/${id}`, data),
  toggleItemActive: (id) => api.patch(`/stationery/items/${id}/toggle`),
  deleteItem: (id) => api.delete(`/stationery/items/${id}`),
  checkItemDelete: (id) => api.get(`/stationery/items/${id}/check-delete`),
  // Do NOT set Content-Type manually — axios/browser must auto-generate the
  // multipart boundary; overriding it strips the boundary and breaks multer.
  bulkUpload:       (formData) => api.post('/stationery/items/bulk-upload', formData),
  downloadTemplate: (category) => api.get('/stationery/items/template', { params: { category }, responseType: 'blob' }),
  // Inventory
  listInventory: (params) => api.get('/stationery/inventory', { params }),
  stockHistory: (params) => api.get('/stationery/inventory/stock-history', { params }),
  adjustInventory: (data) => api.post('/stationery/inventory', data),
  updateInventory: (id, data) => api.put(`/stationery/inventory/${id}`, data),
  bulkStockTemplate: (siteId) => api.get('/stationery/inventory/bulk-stock-template', { params: { siteId }, responseType: 'blob' }),
  bulkStockUpload:   (siteId, formData, dryRun = false) => api.post('/stationery/inventory/bulk-stock-upload', formData, { params: { siteId, dryRun: dryRun ? '1' : undefined } }),
  // Issuances
  listIssuances: (params) => api.get('/stationery/issuances', { params }),
  createIssuance: (data) => api.post('/stationery/issuances', data),
  returnIssuance: (id, data) => api.patch(`/stationery/issuances/${id}/return`, data),
  markRepaired:  (id) => api.patch(`/stationery/issuances/${id}/mark-repaired`),
  markScrapped:  (id) => api.patch(`/stationery/issuances/${id}/mark-scrapped`),
};

// IT — a third item pillar, same shape as Stationery above, but isolated
// behind its own it_view/it_manage/it_add/it_edit/it_issue permissions
// (holding inventory_view/stationery_manage grants nothing here).
export const itApi = {
  // Items
  listItems: (params) => api.get('/it/items', { params }),
  createItem: (data) => api.post('/it/items', data),
  updateItem: (id, data) => api.put(`/it/items/${id}`, data),
  toggleItemActive: (id) => api.patch(`/it/items/${id}/toggle`),
  deleteItem: (id) => api.delete(`/it/items/${id}`),
  checkItemDelete: (id) => api.get(`/it/items/${id}/check-delete`),
  bulkUpload:       (formData) => api.post('/it/items/bulk-upload', formData),
  downloadTemplate: () => api.get('/it/items/template', { responseType: 'blob' }),
  // Inventory
  listInventory: (params) => api.get('/it/inventory', { params }),
  stockHistory: (params) => api.get('/it/inventory/stock-history', { params }),
  adjustInventory: (data) => api.post('/it/inventory', data),
  updateInventory: (id, data) => api.put(`/it/inventory/${id}`, data),
  // Issuances
  listIssuances: (params) => api.get('/it/issuances', { params }),
  createIssuance: (data) => api.post('/it/issuances', data),
  returnIssuance: (id, data) => api.patch(`/it/issuances/${id}/return`, data),
  markRepaired:  (id) => api.patch(`/it/issuances/${id}/mark-repaired`),
  markScrapped:  (id) => api.patch(`/it/issuances/${id}/mark-scrapped`),
};

// Me (self-service profile)
export const meApi = {
  getProfile: () => api.get('/me/profile'),
};
